import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone } from "lucide-react";
import { MaskLines, Overline, Reveal } from "./Motion";
import { IMG, OWNER, PHONE_DISPLAY, PHONE_TEL } from "../../lib/site";

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["30px", "-50px"]);

  return (
    <section id="about" ref={ref} data-testid="about-section" className="relative bg-[#0B0F17] py-24 lg:py-36">
      <div className="mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-8">
        <Reveal className="relative lg:col-span-5">
          <div className="relative mx-auto aspect-[4/5] max-w-md overflow-hidden rounded-t-[999px] rounded-b-[28px] border border-white/10">
            <motion.img src={IMG.floorB} alt="Bright work floor with ergonomic chairs" style={{ y, scale: 1.15 }}
              className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/60 to-transparent" />
          </div>
          <motion.div style={{ y: y2 }}
            className="absolute -bottom-6 right-0 sm:right-6 lg:-right-8 w-40 sm:w-48 overflow-hidden rounded-2xl border border-white/15 shadow-2xl">
            <img src={IMG.idol} alt="Interior details" className="aspect-[4/5] w-full object-cover" loading="lazy" />
          </motion.div>
        </Reveal>
        <div className="lg:col-span-7 lg:pl-8">
          <Overline>About Kinara Works</Overline>
          <MaskLines className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.02] text-white"
            lines={["A Better Place", <>to <span className="text-gold">Work &amp; Grow</span></>]} testId="about-title" />
          <Reveal delay={0.1}>
            <p className="mt-8 text-lg leading-relaxed text-slate-200">
              Whether you are a freelancer looking for a productive desk, a startup building your team, or an established company searching for flexible office space, our co-working spaces are designed to support the way you work.
            </p>
            <p className="mt-5 leading-relaxed text-slate-400">
              We provide modern, comfortable and flexible working environments for individuals and businesses — professional workspaces in convenient Noida locations, with a calm, productive atmosphere from the moment you walk in.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 rounded-[24px] border border-white/10 bg-[#131924] p-6">
            <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#8C6D1F] font-display text-xl font-bold text-[#0B0F17]">AP</div>
            <div className="flex-1">
              <p className="font-mono-k text-[11px] uppercase tracking-[0.25em] text-slate-400">Managed by</p>
              <p className="font-display mt-1 text-xl text-white" data-testid="about-owner-name">{OWNER}</p>
              <p className="mt-1 text-sm text-slate-400">Focused on professional workspaces, convenient locations and a productive environment.</p>
            </div>
            <a href={PHONE_TEL} data-testid="about-call-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 px-5 py-3 text-sm text-[#F1DC8F] hover:bg-[#D4AF37]/10 transition-colors">
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
